import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Clock, Users, BookOpen, Mic, Globe, ChevronRight, Lock, LayoutDashboard, Gamepad2, ArrowRight, FlaskConical, UtensilsCrossed, Award, MessageCircle, PlayCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { safeStorage } from '../lib/safeStorage';
import { trackMarketingEvent } from '../lib/marketingEvents';
import useTheme4A from '../components/useTheme4A';
import { WaveTrail, DotTrail } from '../components/Theme4A';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

const FEATURES = [
  {
    icon: LayoutDashboard,
    label: 'Tracking',
    color: 'blue',
    title: 'Personal Dashboard',
    description: 'Live sobriety clock, weekly progress tracking, daily tasks and direct access to all programme features from one place.'
  },
  {
    icon: BookOpen,
    label: 'Curriculum',
    color: 'indigo',
    title: 'Structured Weekly Programme',
    description: 'Evidence-based curriculum over 32 weeks: the 2-Week Window of Personal History, then 30 Programme Weeks of compulsory readings, self-work assignments and measured progress.'
  },
  {
    icon: Users,
    label: 'Community',
    color: 'teal',
    title: 'Meeting Finder and Verification',
    description: '35+ curated online meetings with attendance verification. Reminders, favourites and selfie proof of attendance.'
  },
  {
    icon: Mic,
    label: 'AI Support',
    color: 'violet',
    title: 'AI Sponsor Bot',
    description: 'Your 24/7 digital sponsor reads material aloud and answers Recovery questions. It is not a clinician or a crisis service.'
  },
  {
    icon: Globe,
    label: 'Accessibility',
    color: 'emerald',
    title: 'Multi-Language Support',
    description: 'Available in English, French, Italian, Korean, Mandarin, Portuguese, Russian, Spanish and Ukrainian for all readings. Self-Work is currently English only.'
  },
  {
    icon: Shield,
    label: 'Resources',
    color: 'sky',
    title: 'Recovery Library',
    description: 'AA, NA and ACA resources with text-to-speech in 9 languages. Weekly readings and downloadable PDF documents.'
  },
  {
    icon: FlaskConical,
    label: 'Evidence',
    color: 'rose',
    title: 'Research Library',
    description: 'Curated peer-reviewed studies on relapse prevention, the neuroscience of alcoholism/addiction and Recovery outcomes. Written in plain English for everyday use.'
  },
  {
    icon: UtensilsCrossed,
    label: 'Wellbeing',
    color: 'amber',
    title: 'Diet and Recovery',
    description: '200 simple, Recovery-friendly recipes across breakfast, lunch, dinner, snacks and smoothies. Includes meal planner, smart shopping list and a "cook with a sponsor" ritual.'
  },
  {
    icon: MessageCircle,
    label: 'Connection',
    color: 'teal',
    title: 'Recovery Room (RR)',
    description: 'Anonymous, moderated chat with sober peers, wherever you are. Auto-defaults to your country room (in your language) and you can join the Global room any time for cross-border support.'
  },
  {
    icon: Award,
    label: 'Motivation',
    color: 'violet',
    title: 'Badges and Rewards',
    description: 'Earn 18 achievement badges for meeting attendance, sobriety streaks, programme milestones and Recovery engagement. Small rewards that build momentum.'
  }
];

const COLOR_MAP = {
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    label: 'text-blue-600',    chip: 'bg-blue-100 text-blue-600' },
  indigo:  { bg: 'bg-indigo-50',  border: 'border-indigo-200',  label: 'text-indigo-600',  chip: 'bg-indigo-100 text-indigo-600' },
  teal:    { bg: 'bg-teal-50',    border: 'border-teal-200',    label: 'text-teal-600',    chip: 'bg-teal-100 text-teal-600' },
  violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  label: 'text-violet-600',  chip: 'bg-violet-100 text-violet-600' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'text-emerald-600', chip: 'bg-emerald-100 text-emerald-600' },
  sky:     { bg: 'bg-sky-50',     border: 'border-sky-200',     label: 'text-sky-600',     chip: 'bg-sky-100 text-sky-600' },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   label: 'text-amber-600',   chip: 'bg-amber-100 text-amber-600' },
  rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',    label: 'text-rose-600',    chip: 'bg-rose-100 text-rose-600' },
};

function FeatureCard({ icon: Icon, title, description, index }) {
  return (
    <div
      className="bg-white border-2 border-black rounded-2xl p-6 transition-all duration-200"
      data-testid={`feature-${index}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-white border-2 border-black flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-black" strokeWidth={1.8} />
        </div>
        <div className="min-w-0">
          <h3 className="text-[17px] font-bold text-black mb-2 leading-snug">{title}</h3>
          <p className="text-base text-black leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
}

function LandingPage() {
  // fixedDecor off so the dot-trail scrolls with the header rather than
  // staying pinned at the top of the viewport and sliding under the
  // sign-up box. The static wave at the end of the page is kept.
  useTheme4A({ fixedDecor: false });
  const navigate = useNavigate();
  const [inviteCode, setInviteCode] = useState('');
  const [showInviteInput, setShowInviteInput] = useState(false);
  const [stats, setStats] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landing/stats`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setStats(d);
      } catch (e) { /* silent: banner is non-critical */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleInviteSubmit = async () => {
    if (!acknowledged) {
      toast.error('Please tick "Before you sign up" first');
      return;
    }
    if (!inviteCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: inviteCode.trim().toUpperCase() })
      });
      if (response.ok) {
        const data = await response.json();
        safeStorage.setItem('invite_verified', 'true');
        // Persist beta-tester invite so signup creates the right role.
        if (data.role_hint === 'beta_tester' && data.invite_code) {
          safeStorage.setItem('beta_invite_code', data.invite_code);
          toast.success('Beta tester access granted!');
        } else {
          safeStorage.removeItem('beta_invite_code');
          toast.success('Access granted!');
        }
        navigate('/login');
      } else {
        toast.error('Invalid invite code. Please check and try again.');
      }
    } catch (error) {
      toast.error('Could not verify code. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900">

      {/* Live Community Pulse Banner: quiet social proof */}
      {stats && (stats.members_total > 0 || stats.cooked_this_week > 0) && (
        <div className="bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950 text-white" data-testid="community-pulse-banner">
          <div className="max-w-5xl mx-auto px-6 py-2.5 flex items-center justify-center gap-x-5 gap-y-1 flex-wrap text-[11px] sm:text-[12px] font-medium tracking-wide">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-zinc-300">Live community</span>
            </span>
            {stats.members_total >= 5 && (
              <span className="hidden sm:inline-flex items-center gap-1.5">
                <span className="text-zinc-500">·</span>
                <span><span className="font-bold text-white">{stats.members_display || stats.members_total}</span> <span className="text-zinc-300">members</span></span>
              </span>
            )}
            {stats.countries_count > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="text-zinc-500">·</span>
                <span><span className="font-bold text-white">{stats.countries_count}</span> <span className="text-zinc-300">{stats.countries_count === 1 ? 'country' : 'countries'}</span></span>
              </span>
            )}
            {stats.cooked_this_week > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="text-zinc-500">·</span>
                <span><span className="font-bold text-white">{stats.cooked_this_week}</span> <span className="text-zinc-300">meals cooked this week</span></span>
              </span>
            )}
            {stats.cook_invites_total > 0 && (
              <span className="hidden sm:inline-flex items-center gap-1.5">
                <span className="text-zinc-500">·</span>
                <span><span className="font-bold text-white">{stats.cook_invites_total}</span> <span className="text-zinc-300">cook-together invites</span></span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="border-b-2 border-black">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <img src={AXIOM_LOGO} alt="Axiom" className="h-8" />
          <button
            onClick={() => setShowInviteInput(true)}
            className="flex items-center gap-2 text-base font-bold text-black hover:underline"
            data-testid="access-btn"
          >
            <Lock className="w-4 h-4" />
            Member access
          </button>
        </div>
      </nav>

      {/* 4a inline dot-trail: scrolls with the header rather than being
          position:fixed, so it does not sit on top of the sign-up box. */}
      <DotTrail inline />

      {/* Hero Section */}
      <header className="bg-white border-b-2 border-black">
        <div className="max-w-5xl mx-auto px-6 pt-16 pb-20 sm:pt-20 sm:pb-24">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.08] text-black mb-6" data-testid="hero-title">
              Axiom Recovery
              <br />
              Programme
            </h1>
            <p className="text-base sm:text-lg text-black leading-relaxed mb-10 max-w-xl">
              A comprehensive Recovery programme of 32 weeks: the 2-Week Window, then 30 Programme Weeks. It is built on evidence-based practice and helps clients from all parts of the community attain sustainable long-term Recovery.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => setShowInviteInput(true)}
                className="h-12 px-7 text-base font-bold rounded-lg border-2 border-black text-black hover:brightness-95"
                style={{ backgroundColor: '#ffcd04' }}
                data-testid="get-started-btn"
                data-arp="btn-primary"
              >
                Get started
                <ChevronRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button
                onClick={() => { trackMarketingEvent('tour_cta_click'); navigate('/tour'); }}
                className="bg-white hover:bg-zinc-50 h-12 px-7 text-base font-bold rounded-lg border-2 border-black text-black group"
                data-testid="watch-tour-btn"
              >
                <PlayCircle className="w-4 h-4 mr-1.5 transition-transform group-hover:scale-110" />
                Watch 60-sec tour
              </Button>
              <Button
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                className="bg-white hover:bg-zinc-50 h-12 px-7 text-base font-bold rounded-lg border-2 border-black text-black"
                data-testid="learn-more-btn"
              >
                Learn more
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Before you go any further — Batch 7 item 1 */}
      <section className="py-16 sm:py-20 px-6 bg-white" data-testid="before-you-go-section">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white border-2 border-black rounded-2xl p-6 sm:p-8 text-black">
            <h2 className="text-2xl sm:text-3xl font-black text-black mb-6">Before you go any further</h2>

            <h3 className="text-base font-bold text-black mb-2">Why you are here</h3>
            <p className="text-base text-black leading-relaxed mb-6">
              You are looking at this programme because drinking and/or using has cost you something. Or you are afraid it will. A court, an employer, a doctor or your family may have sent you here. You may have come on your own. The reason you are here matters less than what you do next.
            </p>

            <h3 className="text-base font-bold text-black mb-2">What this programme IS</h3>
            <p className="text-base text-black leading-relaxed mb-6">
              The Axiom Recovery Programme is a structured, week-by-week programme for Recovery from alcoholism/addiction. It runs for 32 weeks: the 2-Week Window, then 30 Programme Weeks. Each week opens only when the work for the week before is done. Every week asks you to read, think, write and act.
            </p>

            <h3 className="text-base font-bold text-black mb-2">The cost</h3>
            <p className="text-base text-black leading-relaxed mb-6">
              Cost is a common objection to doing this work. You know your alcoholism/addiction has already cost you far more than this programme. Some of those costs are hard to see, such as the damage to your family and your reputation.
            </p>

            <h3 className="text-base font-bold text-black mb-2">What this programme is NOT</h3>
            <p className="text-base text-black leading-relaxed mb-6">
              It is not a cure. It is not a clinician, a crisis service or a substitute for medical care. It will not do the work for you.
            </p>

            <h3 className="text-base font-bold text-black mb-2">No guarantees</h3>
            <p className="text-base text-black leading-relaxed mb-6">
              Nobody can promise you what your Recovery will look like. Not this programme, not a doctor, not a counsellor. Nobody. Some people stay clean and sober from the first day. Some relapse and come back. Some do not come back. What we can tell you is this: the people who do the work give themselves the best chance. So even though we cannot tell you what your Recovery will look like, it will definitely be different.
            </p>

            <h3 className="text-base font-bold text-black mb-2">What we ask of you</h3>
            <ul className="list-disc pl-6 text-base text-black leading-relaxed mb-6 space-y-1">
              <li>Be honest, with yourself first.</li>
              <li>Do the work every week, even when you don't feel like it.</li>
              <li>Go to meetings.</li>
              <li>
                Ask for help when you need it. If you are at risk of harm, tap{' '}
                <button
                  onClick={() => navigate('/get-help')}
                  className="underline font-bold text-black hover:no-underline"
                  data-testid="before-you-go-help-link"
                >
                  "Get help now"
                </button>
                .
              </li>
            </ul>

            <p className="text-base text-black leading-relaxed mb-6">
              If you are not ready to do the work, do not sign up now. Come back when you are ready.
            </p>

            <p className="text-base font-bold text-black">The choice is yours.</p>
          </div>
        </div>
      </section>

      {/* Programme Features */}
      <section id="features" className="py-16 sm:py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-black text-black mb-3">Programme features</h2>
            <p className="text-base text-black max-w-lg">
              Everything you need for a structured, supported Recovery journey. Built with clinical expertise and modern technology.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {FEATURES.map((feature, i) => (
              <FeatureCard key={i} {...feature} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Programme Structure */}
      <section className="py-16 sm:py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-black text-black mb-3">The 32-week programme</h2>
            <p className="text-base text-black max-w-lg">
              A carefully designed curriculum that guides you from initial assessment through to long-term sustainable Recovery.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {/* Phase 1 */}
            <div className="bg-white border-2 border-black rounded-2xl p-6">
              <div className="w-9 h-9 rounded-lg bg-white border-2 border-black flex items-center justify-center mb-4">
                <Clock className="w-4.5 h-4.5 text-black" strokeWidth={1.8} />
              </div>
              <h3 className="text-base font-bold text-black mb-2">2-Week Window</h3>
              <p className="text-base text-black leading-relaxed">
                Complete your Personal History assessment, familiarise yourself with the programme and establish your baseline. Meet your AI sponsor.
              </p>
            </div>
            {/* Phase 2 */}
            <div className="bg-white border-2 border-black rounded-2xl p-6">
              <div className="w-9 h-9 rounded-lg bg-white border-2 border-black flex items-center justify-center mb-4">
                <BookOpen className="w-4.5 h-4.5 text-black" strokeWidth={1.8} />
              </div>
              <h3 className="text-base font-bold text-black mb-2">Weeks 1 to 15</h3>
              <p className="text-base text-black leading-relaxed">
                Core Recovery education with weekly readings, meeting attendance, journaling and progressive self-work assignments.
              </p>
            </div>
            {/* Phase 3 */}
            <div className="bg-white border-2 border-black rounded-2xl p-6">
              <div className="w-9 h-9 rounded-lg bg-white border-2 border-black flex items-center justify-center mb-4">
                <Award className="w-4.5 h-4.5 text-black" strokeWidth={1.8} />
              </div>
              <h3 className="text-base font-bold text-black mb-2">Weeks 16 to 30</h3>
              <p className="text-base text-black leading-relaxed">
                Advanced Recovery skills, relapse prevention strategies and building sustainable long-term Recovery foundations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 px-6 bg-white">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-black text-black mb-3">Ready to begin?</h2>
          <p className="text-base text-black mb-6">
            The Axiom Recovery Programme is currently in private beta.
            Enter your invite code to access the full programme.
          </p>
          <div className="max-w-sm mx-auto mb-6 border-2 border-black rounded-[10px] bg-white p-4 text-left" data-testid="landing-price-card">
            <p className="text-base font-bold text-black">AUD 24.70 a week or AUD 690 upfront.</p>
            <p className="text-base font-bold text-black">No refunds for change of mind. Lifetime access comes with the upfront payment or after 28 weekly payments.</p>
          </div>
          <div className="bg-white border-2 border-black rounded-2xl p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto">
              <Input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="Invite code"
                className="bg-white border-2 border-black text-black placeholder:text-zinc-500 h-12 text-center tracking-[0.3em] font-mono text-sm"
                data-testid="invite-code-input"
                onKeyDown={(e) => e.key === 'Enter' && handleInviteSubmit()}
              />
            </div>

            {/* "Before you sign up" notice + required tick box — Feb 2026 brief.
                Sits DIRECTLY above the payment / Enter button. Button is
                disabled until the box is ticked. No extra screen. */}
            <div className="max-w-sm mx-auto mt-4 bg-white border-2 border-black rounded-lg p-3 text-left" data-testid="landing-signup-notice">
              <p className="text-base font-bold text-black mb-1">Before you sign up</p>
              <p className="text-base text-black leading-relaxed">
                The Axiom Recovery Programme is a self-paced educational and support programme for alcoholism/addiction. It is not a clinician, a crisis service or a substitute for medical care. If you are at risk of harm to yourself or someone else, call your local emergency number or tap "Get help now".
              </p>
              <p className="text-base text-black leading-relaxed mt-2">
                Recovery takes commitment from day one. The programme only works if you do the work.
              </p>
              <p className="text-base text-black leading-relaxed mt-2">
                No refunds for change of mind. This does not affect your rights under the Australian Consumer Law.
              </p>
              <p className="text-base text-black leading-relaxed mt-2">
                Lifetime access comes with the upfront payment or after 28 weekly payments.
              </p>
              <label className="mt-3 flex items-start gap-2 cursor-pointer" data-testid="landing-ack-label">
                <input
                  type="checkbox"
                  className="mt-1 w-5 h-5"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  data-testid="landing-ack-checkbox"
                />
                <span className="text-base text-black">
                  I have read and understand this.
                </span>
              </label>
            </div>

            <div className="max-w-sm mx-auto mt-4">
              <Button
                onClick={handleInviteSubmit}
                disabled={!acknowledged}
                className="w-full h-12 px-6 text-base font-bold whitespace-nowrap rounded-lg border-2 border-black text-black hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: acknowledged ? '#ffcd04' : '#ffffff' }}
                data-testid="invite-submit-btn"
                data-arp="btn-primary"
              >
                Enter
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>

            <button
              onClick={() => navigate('/login')}
              className="text-base text-black hover:underline mt-5 font-semibold inline-block"
              data-testid="cta-direct-login-link"
            >
              Already have an account? Sign in directly
            </button>
          </div>
        </div>
      </section>

      {/* Invite Code Modal */}
      {showInviteInput && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowInviteInput(false)}
        >
          <div
            className="bg-white border border-zinc-200 rounded-2xl p-8 max-w-sm w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-600 block mb-2">
                Invite Only
              </span>
              <h3 className="text-lg font-bold text-zinc-900 mb-1">Member Access</h3>
              <p className="text-[13px] text-zinc-500">Enter your invite code to access the Axiom Recovery Programme</p>
            </div>
            <Input
              type="text"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="Invite code"
              className="bg-white border-2 border-black text-black placeholder:text-zinc-500 h-14 text-center text-lg tracking-[0.4em] font-mono mb-4"
              data-testid="modal-invite-input"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleInviteSubmit()}
            />

            {/* "Before you sign up" notice + required tick box — mirrors the CTA. */}
            <div className="mb-4 bg-white border-2 border-black rounded-lg p-3 text-left">
              <p className="text-base font-bold text-black mb-1">Before you sign up</p>
              <p className="text-sm text-black leading-relaxed">
                The Axiom Recovery Programme is a self-paced educational and support programme for alcoholism/addiction. It is not a clinician, a crisis service or a substitute for medical care. If you are at risk of harm to yourself or someone else, call your local emergency number or tap "Get help now".
              </p>
              <p className="text-sm text-black leading-relaxed mt-2">
                Recovery takes commitment from day one. The programme only works if you do the work.
              </p>
              <p className="text-sm text-black leading-relaxed mt-2">
                No refunds for change of mind. This does not affect your rights under the Australian Consumer Law.
              </p>
              <p className="text-sm text-black leading-relaxed mt-2">
                Lifetime access comes with the upfront payment or after 28 weekly payments.
              </p>
              <label className="mt-2 flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 w-5 h-5"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  data-testid="modal-ack-checkbox"
                />
                <span className="text-sm text-black">
                  I have read and understand this.
                </span>
              </label>
            </div>

            <Button
              onClick={handleInviteSubmit}
              disabled={!acknowledged}
              className="w-full h-12 text-base font-bold mb-3 rounded-lg border-2 border-black text-black hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: acknowledged ? '#ffcd04' : '#ffffff' }}
              data-testid="modal-invite-submit"
              data-arp="btn-primary"
            >
              Access Programme
            </Button>
            <button
              onClick={() => setShowInviteInput(false)}
              className="w-full text-base text-black hover:underline py-2"
            >
              Cancel
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full text-base text-black hover:underline py-2 font-semibold"
              data-testid="direct-login-link"
            >
              Already have an account? Sign in
            </button>
          </div>
        </div>
      )}

      {/* 4a: static wave strip + blue footer (A6 fallback for pages
          without a bottom menu bar). The footer background becomes blue
          with white text; nothing else inside the footer changes. */}
      <WaveTrail variant="static" />
      <footer className="theme-4a-footer-blue py-8 px-6 bg-white">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={AXIOM_LOGO} alt="Axiom" className="h-6" />
          <p className="text-base text-black">
            Axiom Recovery Programme. A product of The Silkworth Institute.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
